import { useEffect, useState, type ComponentPropsWithoutRef } from "react"

import { cn } from "@/lib/utils"

interface MarqueeProps extends ComponentPropsWithoutRef<"div"> {
  /**
   * Optional CSS class name to apply custom styles
   */
  className?: string
  /**
   * Whether to reverse the animation direction
   * @default false
   */
  reverse?: boolean
  /**
   * Whether to pause the animation on hover
   * @default false
   */
  pauseOnHover?: boolean
  /**
   * Content to be displayed in the marquee
   */
  children: React.ReactNode
  /**
   * Whether to animate vertically instead of horizontally
   * @default false
   */
  vertical?: boolean
  /**
   * Number of times to repeat the content
   * @default 4
   */
  repeat?: number
}

export function Marquee({
  className,
  reverse = false,
  pauseOnHover = false,
  children,
  vertical = false,
  repeat = 4,
  ...props
}: MarqueeProps) {
  // The server (and the hydrating client) render the content once, standing
  // still. The extra copies that make the loop seamless only exist after
  // mount, aria-hidden and inert, so search engines and screen readers get
  // each item once and the SSR HTML doesn't carry it `repeat` times. The
  // animation starts together with the copies: all of them start in sync at
  // translateX(0), which is where the lone static copy already sits, so
  // nothing jumps.
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])
  const copies = mounted ? repeat : 1

  return (
    <div
      {...props}
      className={cn(
        "group flex gap-(--gap) overflow-hidden p-2 [--duration:40s] [--gap:1rem]",
        {
          "flex-row": !vertical,
          "flex-col": vertical,
        },
        className
      )}
    >
      {Array.from({ length: copies }, (_, i) => (
        <div
          key={i}
          aria-hidden={i > 0 || undefined}
          inert={i > 0 || undefined}
          className={cn("flex shrink-0 justify-around gap-(--gap)", {
            "flex-row": !vertical,
            "flex-col": vertical,
            "animate-marquee": mounted && !vertical && !reverse,
            "animate-marquee-vertical": mounted && vertical && !reverse,
            // Use a full `animation` shorthand for the reverse case so the
            // direction isn't reset by the base animate-marquee utility.
            "[animation:marquee_var(--duration)_linear_infinite_reverse]":
              mounted && !vertical && reverse,
            "[animation:marquee-vertical_var(--duration)_linear_infinite_reverse]":
              mounted && vertical && reverse,
            "group-hover:[animation-play-state:paused]": pauseOnHover,
          })}
        >
          {children}
        </div>
      ))}
    </div>
  )
}
